<template>
  <div class="py-2 my-2 help-container">
    <h1 class="separator py-4">Bulk Budget Creation</h1>
    <span class="banner py-3 mb-4">
      <div>This tool will allow you to use two templates (GL Account and Settings) to create budgets for multiple properties.</div>
      <br/>
      <div>
        Expand the topic below for more information on that step.
      </div>
    </span>

    <!--Help Sections-->
    <v-expansion-panels v-model="openSectionInternal">
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(0)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(0)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Steps to Create Budgets in Bulk</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>Once you select the GLOBAL option at the top of the page, you will choose Bulk Budget Creation from the left-side menu.</div>
          <div>From here you will be able to select the following options to determine which budgets are created:</div>
          <ul>
            <li><strong>Year</strong> - You will select the year of the budgets to be created.</li>
            <li><strong>Start Month</strong> - You will select the month that each budget will start.</li>
            <li><strong>Accounting Basis</strong> - You will select whether these budgets will be Accrual or Cash based.</li>
            <li><strong>GL Account Template</strong> - You will select which GL Account template to use (which will organize your GL Accounts).</li>
            <li><strong>Settings Template</strong> - You will select the Settings template (which will put the various settings in place).</li>
            <li><strong>Apply to</strong> - You will select the properties or property groups.</li>
          </ul>
          <img src="../../assets/help/bulkbudcreat1.png" />
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(1)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(1)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Possible Errors</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>You may receive an error in certain situations:</div>
          <ul>
            <li>The combination of year, start month, and accounting basis already exists for this property.  If you receive this message, the budget file already exists and does not need to be created.</li>
            <li>The property has not been included in the GL Account template that was selected.  If you receive this message, go to Templates > Click into GL Account template (left-side menu) > Click EDIT for the template > Click Edit Template. Add the desired property to the left-side window. You can now either add the budget file manually or go through Bulk Budget Creation, but only select this property.</li>
            <li>
              A transaction category selected in the Settings Template is not available to this property.  If you receive this message:
              <ul>
                <li>Go to Templates > Click into Settings Template (left-side menu) > Click EDIT for the template > Look at the Transaction Categories selected under the Lease Calculations and Other Concessions tabs.</li>
                <li>Then go to the main ResMan page > Admin > Transaction Categories.  Make sure that every one of those Transaction Categories used is available to each one of those properties by calling up each Transaction Category and looking at the Properties module.</li>
              </ul>
            </li>
            <li>
              The property does not have the necessary accounting periods. If you receive this message, it means that for your budget year, you do not have all twelve periods. To create the periods follow these steps:
              <ul>
                <li>
                  Go to the main ResMan page > Admin > Periods. Choose “Add Future.”
                  <ul>
                    <li>Under Accounting Period, select the most future month in your budget (you can create periods further into the future if desired).</li>
                    <li>End Date:  Select “Use property default end date”</li>
                    <li>Select the property and click SAVE</li>
                  </ul>
                </li>
                <li>
                  Go to the main ResMan page > Admin > Properties.
                  <ul>
                    <li>Select the property</li>
                    <li>Near the bottom of the left-side menu, click “Add Periods”</li>
                    <li>You may receive a message that all periods exist, but if not, Select the month that is furthest in the future and click OK</li>
                  </ul>
                </li>
              </ul>
            </li>
            <li>
              The Bad Debt GL Account selected in the Settings Template is not available to this property.  If you receive this message:
              <ul>
                <li>Go to Templates > Click into Settings Template (left-side menu) > Click EDIT for the template > Look at the GL Account selected for Bad Debt in the Other Concessions tab. </li>
                <li>Then go to the main ResMan page > Admin > GL Property Permissions.  Enter the GL account selected for Bad Debt and make sure that all of the property are marked “YES” that they have access to that account.</li>
              </ul>
            </li>
          </ul>
          <br />
          <img src="../../assets/help/bulkbudcreat2.png" />
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(2)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(2)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Previously Applied Section</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>After this process has been run, ResMan will store that information.</div>
          <div>Select the year, and any Bulk Budget Creation that has been run will display.</div>
          <br />
          <img src="../../assets/help/bulkbudcreat3.png" />
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Prefer to go to the online help article? <a @click="openExternalTopic('BudgetsPro Bulk Budget Creation')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Want to see all BudgetsPro help articles? <a @click="openExternalTopic('BudgetsPro')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
  import HelpMixin from '@/mixins/Help.js'
  export default {
    name: 'BulkBudgetCreationHelp',
    mixins: [HelpMixin]
  }
</script>

<style lang="scss" scoped>
  .no-bullet {
    list-style-type: none;
  }
  img {
      max-width: 650px;
  }
  img.small {
      width: 35px;
  }
  .icon {
    order: 0;
  }

  .header {
    order: 1;
  }

  .help-container {
    border-bottom: 1px solid var(--v-resGrey-lighten1)
  }

  .help-content div,
  .help-content li {
    margin-top: 10px !important;
    margin-bottom: 10px !important;
  }

  .banner {
    text-align: center;
    background-color: var(--v-resGrey-lighten1);
    display: block;
  }

  .pointer {
    cursor: pointer !important;
  }

  .separator {
    display: flex;
    align-items: center;
    text-align: center;
  }

  .separator::before,
  .separator::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid var(--v-resGrey-darken1);
  }

  .separator:not(:empty)::before {
    margin-right: .25em;
  }

  .separator:not(:empty)::after {
    margin-left: .25em;
  }
</style>
